/*---
** Attribute Config Example for Form Fields **
data-field-{name}="{type}, {label}, {placeholder or [select options, pipe delimited, ex: One|Two|Three]}, {default value}, {required (boolean)}"

** Other data attributes **
data-form-id: Required Hubspot form id
data-button-color: Optional brand color, e.g. blue-jeans
data-button-label-color: Optional brand color, e.g. white
data-tracking: Optional, see trackingEvents obj below for options, default is "leadGen"
data-msg: Optional single line message to display in body text of submission "thank you" message
data-event: Optional event name to be dispatched after successful submission
---*/

import { BaseWC } from './BaseWC'
import './ZeroIllustration'
import { sendLeadGenEvent, sendContactUsEvent, sendGatedResUnlockEvent, sendProviderListReqEvent } from '../scripts/modules/utmTracking'

const states = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming']

class ZeroForm extends BaseWC {
    trackingEvents = {
        contactUs: sendContactUsEvent,
        leadGen: sendLeadGenEvent,
        resourceGate: sendGatedResUnlockEvent,
        providerListing: sendProviderListReqEvent
    }

    constructor() {
        super()
        this.submitButton = this.shadowRoot.querySelector('[data-submit]')
        this.handleSubmission()
    }

    input(props) {
        return `
            <fieldset>
                <label for="${props.name}">${props.label} ${this.optionalLabel(props.required)}</label>
                <input 
                type="${props.type}" 
                placeholder="${props.optionsPh}" 
                spellcheck="off" 
                name="${props.name}"
                id="${props.name}" 
                ${(props.required === 'true') ? 'required' : ''}
                value="${props.defaultValue}">
            </fieldset>
        `
    }

    textArea(props) {
        return `
            <fieldset>
                <label for="${props.name}">${props.label} ${this.optionalLabel(props.required)}</label>
                <textarea 
                type="${props.type}" 
                placeholder="${props.optionsPh}" 
                name="${props.name}"
                id="${props.name}" 
                ${(props.required === 'true') ? 'required' : ''}
                value="${props.defaultValue}"></textarea>
            </fieldset>
        `
    }

    select(props, optionsOverride = false) {
        const options = (optionsOverride) ? optionsOverride : props.optionsPh
        return `
            <fieldset>
                <label for="${props.name}">${props.label} ${this.optionalLabel(props.required)}</label>
                <select name="${props.name}" id="${props.name}" ${(props.required === 'true') ? 'required' : ''}>
                    <option disabled value="" selected>Please Choose</option>
                    ${options.map(item => `<option value="${item}" ${(props.defaultValue === item) ? 'selected' : ''}>${item}</option>`)}
                </select>
            </fieldset>
        `
    }

    checkbox(props) {
        return `
            <fieldset class="checkbox">
                <input 
                type="checkbox" 
                name="${props.name}"
                id="${props.name}"
                ${(props.defaultValue === 'checked') ? 'checked="true"' : ''}>
                <label for="${props.name}">${props.label} ${this.optionalLabel(props.required)}</label>
            </fieldset>
        `
    }

    route(props) {
        if(props.type === 'text') return this.input(props)
        if(props.type === 'hidden') return this.input(props)
        if(props.type === 'number') return this.input(props)
        if(props.type === 'email') return this.input(props)
        if(props.type === 'select') return this.select(props)
        if(props.type === 'checkbox') return this.checkbox(props)
        if(props.type === 'textarea') return this.textArea(props)
        if(props.type === 'state') return this.select(props, states)
    }

    optionalLabel(val) {
        if(JSON.parse(val)) return ''
        return `<i>(optional)</i>`
    }

    parseConfig(key, val) {
        const arr = val.split(/\,\s/g)

        return {
            type: arr[0],
            name: key.replace('field', '').toLowerCase(),
            label: arr[1],
            optionsPh: this.parseOptions(arr[2]),
            defaultValue: arr[3].replace(`''`, ''),
            required: arr[4]
        }
    }

    parseOptions(str) {
        if(!str.match('|')) return str

        return str.split('|')
    }

    getFields() {
        const markup = []
        const fields = Object.keys(this.dataset).filter(item => item.match('field')).map(item => {
            return this.parseConfig(item, this.dataset[item])
        })

        fields.forEach(item => {
            markup.push(this.route(item))
        })

        return markup.join('')
    }

    getCookie(name) {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)

        if(parts.length == 2) return parts.pop().split(';').shift()    
        return ''
    }

    eventTracking() {
        if(this.hasAttribute('data-tracking')) {
            this.trackingEvents[this.dataset.tracking]()
            return
        }

        this.trackingEvents.leadGen('Submitted Form')
    }

    fireEvent() {
        const eventName = (this.hasAttribute('data-event')) ? this.dataset.event : 'zeroFormSubmitted'
        this.dispatchEvent(new CustomEvent(eventName, { bubbles: true }))
    }

    async hubspotSubmission() {
        const payload = {}
        const portalId = '4141657'
        const apiURL = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${this.dataset.formId}`

        // Submission time
        payload.submittedAt = Date.now()

        // Attempt to fetch IP address
        const ipRes = await fetch('/wp-content/themes/ZERO/api/ip-location.php')
        const ipData = (ipRes.ok) ? await ipRes.json() : { ip: '127.0.0.1' }

        // Cookie and page data
        payload.context = {
            pageUri: window.location.href,
            pageName: document.title,
            ipAddress: ipData.ip
        }
        
        if(this.getCookie('hubspotutk')) payload.context.hutk = this.getCookie('hubspotutk')
        
        // Form fields and values
        const formData = new FormData(this.shadowRoot.querySelector('[data-form]'))
        payload.fields = []
        Array.from(formData.entries())
        .map(entry => { 
            payload.fields.push({
                objectTypeId: '0-1',
                name: entry[0],
                value: entry[1]
            })
        })

        // Localhost intercept
        if(window.location.href.match('localhost')) {
            console.log(payload)
            return true
        }
        
        // Submission request
        const res = await fetch(apiURL, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json'
            }
        }) 

        if(!res.ok) return false

        if(res.ok) return true
    }

    async handleSubmission() {
        const label = this.submitButton.textContent
        
        this.shadowRoot.querySelector('form').addEventListener('submit', async event => {
            event.preventDefault()
            
            // Disable submit button during request
            this.submitButton.setAttribute('disabled', true)
            this.submitButton.textContent = 'Please Wait...'

            const res = await this.hubspotSubmission()
            
            if(!res) {
                alert('There was an issue submitting the form. Please try again.')
                this.submitButton.removeAttribute('disabled')
                this.submitButton.textContent = label
            }

            if(res) {
                this.submitButton.removeAttribute('disabled')
                this.submitButton.textContent = label
                this.fireEvent()
                this.thankYouMessage()
                this.eventTracking()
            }
        })
    }

    thankYouMessage() {
        this.shadowRoot.querySelector('[data-form]').innerHTML = String.raw`
            <div class="thanks">
                <zero-illus name="eligible-anim"></zero-illus>
                <div>
                    <h3>${(this.hasAttribute('data-msg')) ? this.dataset.msg :  `We'll be in touch with you shortly`}</h3>
                </div>
            </div>
        `
    }

    render() {
        return `
            <style>
                :host {
                    --color: var(--color-1);
                    --fill: var(--neutral-1);
                    --stroke: var(--neutral-2);
                    --focus: var(--color-1-lt);

                    --field-height: 4.6rem;
                }

                * {
                    box-sizing: border-box;
                }

                form, .fields {
                    display: grid;
                    gap: 1rem;
                    margin: 0;
                    padding: 0;
                }

                ::placeholder {
                    color: var(--neutral-3);
                    font-style: italic;
                    opacity: 0.7;
                }

                .fields {
                    margin-bottom: 1rem;
                }

                :host([name-line]) .fields {
                    grid-template-columns: 1fr 1fr;
                }

                :host([name-line]) fieldset:not(:is(:nth-child(1), :nth-child(2))) {
                    grid-column: 1/-1;
                }

                fieldset {
                    all: unset;
                }

                fieldset:has(input[type=hidden]) {
                    display: none;
                }

                .checkbox {
                    display: flex;
                    gap: 1em;
                }

                label {
                    color: var(--color);
                    display: block;
                    font-size: 1.4rem;
                    font-weight: 700;
                    margin-bottom: 0.3em;
                    -webkit-user-select: none;
                    user-select: none;
                }

                label i {
                    font-weight: 400;
                    opacity: 0.7;
                }

                input, 
                textarea, 
                select {
                    all: unset;
                    background-color: var(--fill);
                    border: 0.2rem solid var(--stroke);
                    border-radius: 0.2em;
                    box-sizing: border-box;
                    color: var(--color);
                    font-size: var(--txt-body);
                    height: var(--field-height);
                    line-height: var(--field-height);
                    padding-inline: 0.5em;
                    -webkit-user-select: none;
                    user-select: none;
                }

                :host(.visually-impaired) input,
                :host(.visually-impaired) textarea, 
                :host(.visually-impaired) select {
                    border-color: var(--color-1);
                }

                input:focus, 
                textarea:focus, 
                select:focus {
                    border-color: var(--focus);
                }

                input {
                    width: 100%;
                }

                input[type=checkbox] {
                    background-color: var(--fill);
                    border-radius: 0.4ch;
                    cursor: pointer;
                    display: inline-grid;
                    height: 2ch;
                    max-width: 2ch;
                    min-width: 2ch;
                    padding: 0;
                    place-items: center;
                    transition: background 100ms ease;
                    width: 2ch;
                }

                input[type=checkbox]::before {
                    background-image: url('data:image/svg+xml,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" xml:space="preserve" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2"><path style="fill:none" d="M-850-1316.67H525.15V275H-850z"/><path d="M39.688 56.226 21.947 38.485a4.078 4.078 0 0 0-5.766 0L4.27 50.397a4.076 4.076 0 0 0 0 5.766l32.535 32.535a4.076 4.076 0 0 0 5.766 0l11.912-11.911.047-.048 41.2-41.2a4.077 4.077 0 0 0 0-5.767L83.819 17.861a4.078 4.078 0 0 0-5.766 0L39.688 56.226z" style="fill:%23ffffff"/></svg>');
                    background-repeat: no-repeat;
                    background-size: contain;
                    content: '';
                    display: block;
                    height: 1.3ch;
                    opacity: 0;
                    scale: 0;
                    transition: scale 300ms ease, opacity 100ms ease;
                    width: 1.3ch;
                }

                input[type=checkbox]:checked {
                    background-color: var(--color-2);
                }

                input[type=checkbox]:checked::before {
                    opacity: 1;
                    scale: 1;
                }

                select {
                    background-image: url('data:image/svg+xml,<svg width="100%" height="100%" viewBox="0 0 67 67" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:3;"><path d="M16.667,29.167l16.666,12.5l16.667,-12.5" style="fill:none;stroke:%23003980;stroke-width:12.5px;"/><g id="Logo-Dark" serif:id="Logo Dark"></g><g id="logo-redraw" serif:id="logo redraw"></g></svg>');
                    background-position: right 0.7em center;
                    background-repeat: no-repeat;
                    background-size: 1.5ch auto;
                    padding-right: 2.1em;
                    width: 100%;
                }

                :host(.dark) select {
                    background-image: url('data:image/svg+xml,<svg width="100%" height="100%" viewBox="0 0 67 67" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:3;"><path d="M16.667,29.167l16.666,12.5l16.667,-12.5" style="fill:none;stroke:%23ffffff;stroke-width:12.5px;"/><g id="Logo-Dark" serif:id="Logo Dark"></g><g id="logo-redraw" serif:id="logo redraw"></g></svg>');
                }

                textarea {
                    line-height: 1.5;
                    min-height: 10rem;
                    padding-block: 0.5em;
                    width: 100%;
                }

                @keyframes popIn {
                    from {
                        opacity: 0;
                        scale: 0.3;
                    }

                    to {
                        opacity: 1;
                        scale: 1;
                    }
                }

                .thanks {
                    animation: popIn 500ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    animation-fill-mode: both;
                    align-items: center;
                    background-color: var(--content);
                    border: 0.2rem solid var(--neutral-2);
                    border-radius: 1rem;
                    color: var(--color);
                    display: grid;
                    gap: var(--gap-sm);
                    grid-template-columns: 1fr 2fr;
                    padding: 2em;
                }

                .thanks h3 {
                    line-height: 1.2;
                    margin: 0;
                }

                .thanks p {
                    margin-bottom: 0;
                }

                /* Buttons */
                .button {
                    all: unset;
                    background-color: var(--color-1);
                    border-radius: 0.8rem;
                    box-sizing: border-box;
                    color: var(--color-1);
                    cursor: pointer;
                    display: inline-block;
                    font-family: var(--font);
                    font-size: 1.4rem;
                    font-weight: 700;
                    height: var(--field-height);
                    line-height: var(--field-height);
                    letter-spacing: 0.05em;
                    min-width: 20rem;
                    padding: 0 2em;
                    text-align: center;
                    text-decoration: none;
                    text-transform: uppercase;
                    transform: translateY(0);
                    transition: background 200ms ease-out, color 200ms ease-out;
                    user-select: none;
                    white-space: nowrap;
                }

                .button {
                    color: white;
                }

                .button:hover,
                .button:focus {
                    background-color: var(--color-4);
                    color: var(--color-1);
                }

                .button:active {
                    box-shadow: 0 0.5rem 2rem hsla(0, 0%, 0%, 0.2);
                    transform: translateY(0.2rem);
                }

                @media (max-width: 600px) {
                    .thanks {
                        grid-template-columns: 1fr;
                        text-align: center;
                    }

                    :host([name-line]) .fields {
                        grid-template-columns: 1fr;
                    }

                    :host([name-line]) fieldset {
                        grid-column: span 1;
                    }
                }
            </style>

            <form class="form" data-form aria-live="polite">
                <div class="fields">
                    ${this.getFields()}
                </div>

                <button class="button ${(this.hasAttribute('data-button-color')) ? 'bg-' + this.dataset.buttonColor : 'bg-color-2'} ${(this.hasAttribute('data-button-label-color')) ? 'txt-' + this.dataset.buttonLabelColor : 'txt-white'}" tabindex="0" data-submit>${this.dataset.label ?? 'Submit'}</button>
            </form>
        `
    }
}

customElements.define('zero-form', ZeroForm)